import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { AuthImage } from "@/components/AuthImage";
import { Flag } from "@/components/Flag";
import { READ_VOICES, VoicePreview, SpeedSelect } from "@/components/VoiceControls";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  ArrowLeft, Loader2, MessageSquare, Share2, Images, RefreshCw, Languages, FileDown,
  Headphones, Sparkles, Pause, Play,
} from "lucide-react";
import { toast } from "sonner";

const VOICES = READ_VOICES;

export default function StoryDetail() {
  const { storyId } = useParams();
  const nav = useNavigate();
  const [story, setStory] = useState(null);
  const panels = useMemo(
    () => [...(story?.panels || [])].sort((a, b) => a.index - b.index),
    [story]
  );
  const [busy, setBusy] = useState(false);
  const [languages, setLanguages] = useState([]);
  const [lang, setLang] = useState("");
  const [view, setView] = useState(null);
  const [voice, setVoice] = useState(localStorage.getItem("cml_read_voice") || "nova");
  const [speed, setSpeed] = useState(Number(localStorage.getItem("cml_read_speed")) || 1);
  const [audioUrl, setAudioUrl] = useState(null);
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/stories/${storyId}`);
      setStory(data);
      return data;
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
      return null;
    }
  };

  useEffect(() => {
    load();
    api.get("/languages").then(({ data }) => setLanguages(data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storyId]);

  const pending = story && (story.cover_status === "pending" || story.panels_status === "running");

  useEffect(() => {
    if (!pending) return;
    let polls = 0;
    const id = setInterval(() => {
      polls += 1;
      if (polls > 30) return clearInterval(id);
      load();
    }, 6000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pending]);

  useEffect(() => {
    localStorage.setItem("cml_read_speed", String(speed));
    if (audioRef.current) audioRef.current.playbackRate = speed;
  }, [speed]);

  const drawPanels = async () => {
    setBusy(true);
    try {
      const force = story.panels_status === "done";
      await api.post(`/stories/${storyId}/panels${force ? "?force=true" : ""}`);
      toast.success("Drawing your panels — they'll appear as they finish");
      await load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const regenCover = async () => {
    setBusy(true);
    try {
      await api.post(`/stories/${storyId}/cover`);
      toast.success("Illustrating a new cover…");
      await load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const translate = async (code) => {
    setLang(code);
    if (!code) return setView(null);
    setBusy(true);
    try {
      const { data } = await api.post(`/stories/${storyId}/translate`, { language: code });
      setView({ title: data.title, text: data.text, language: code });
      setAudioUrl(null);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
      setLang("");
    } finally {
      setBusy(false);
    }
  };

  const narrate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/stories/${storyId}/narrate`, {
        voice, language: view?.language || null,
      });
      const res = await api.get(`/creations/${data.creation_id}/file`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      setAudioUrl(url);
      setTimeout(() => audioRef.current?.play(), 100);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Narration failed");
    } finally {
      setBusy(false);
    }
  };

  const downloadPdf = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(
        `/stories/${storyId}/pdf${view?.language ? `?language=${view.language}` : ""}`,
        { responseType: "blob" }
      );
      const href = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = href;
      a.download = `${storyId}.pdf`;
      a.click();
      URL.revokeObjectURL(href);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not build the PDF");
    } finally {
      setBusy(false);
    }
  };

  const writeSequel = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/stories/${storyId}/sequel`);
      toast.success("Your character is back — keep the series going");
      nav(`/chat?session=${data.session_id}`);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const share = async () => {
    const url = `${window.location.origin}/api/share/s/${story.share_id}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Share link copied");
    } catch {
      toast.info(url);
    }
  };

  if (!story) {
    return (
      <div className="mx-auto max-w-4xl px-5 py-16" data-testid="story-detail-loading">
        <Loader2 className="h-6 w-6 animate-spin text-brand" />
      </div>
    );
  }

  const shownTitle = view?.title || story.title;
  const shownText = view?.text || story.text;
  const pill = "inline-flex min-h-[44px] items-center rounded-full bg-white/10 px-5 text-sm hover:bg-white/20 disabled:opacity-50";

  return (
    <div className="mx-auto max-w-4xl px-5 py-12 sm:px-8" data-testid="story-detail-page">
      <Link to="/stories" data-testid="story-detail-back" className="inline-flex items-center gap-2 text-sm text-ink-soft hover:text-brand">
        <ArrowLeft className="h-4 w-4 rtl:rotate-180" /> Story Library
      </Link>

      <div className="mt-6 overflow-hidden rounded-3xl border border-white/10 bg-panel">
        <div className="flex aspect-[16/9] items-center justify-center bg-page">
          {story.cover_creation_id ? (
            <AuthImage
              creationId={story.cover_creation_id}
              alt={story.title}
              className="h-full w-full object-cover"
              testid="story-detail-cover"
            />
          ) : (
            <span className="flex items-center gap-2 text-sm text-ink-soft">
              {story.cover_status === "pending" ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin text-brand" /> Illustrating the cover…
                </>
              ) : (
                "No cover yet"
              )}
            </span>
          )}
        </div>

        <div className="p-6 sm:p-8">
          <div className="flex flex-wrap items-center gap-2 text-xs text-ink-soft">
            {story.series_index > 1 && (
              <span data-testid="story-series-badge" className="rounded-full bg-forest/20 px-2.5 py-1 font-semibold text-forest">
                Part {story.series_index}
              </span>
            )}
            <span>{(story.created_at || "").slice(0, 10)}</span>
          </div>
          <h1 dir="auto" data-testid="story-detail-title" className="mt-2 font-display text-2xl font-extrabold tracking-tighter sm:text-3xl">
            {shownTitle}
          </h1>

          <div className="mt-5 flex flex-wrap gap-2">
            <Button asChild variant="secondary" className="min-h-[44px] rounded-full bg-white/10 hover:bg-white/20">
              <Link to={`/chat?session=${story.session_id}`} data-testid="story-continue-button">
                <MessageSquare className="me-2 h-4 w-4" /> Continue in chat
              </Link>
            </Button>
            <button data-testid="story-sequel-button" onClick={writeSequel} disabled={busy} className={pill}>
              <Sparkles className="me-2 h-4 w-4" /> Write the next story
            </button>
            <button data-testid="story-pdf-button" onClick={downloadPdf} disabled={busy} className={pill}>
              <FileDown className="me-2 h-4 w-4" /> Download PDF
            </button>
            <button data-testid="story-share-button" onClick={share} className={pill}>
              <Share2 className="me-2 h-4 w-4" /> Copy share link
            </button>
            <button
              data-testid="story-cover-regen-button"
              onClick={regenCover}
              disabled={busy || story.cover_status === "pending"}
              className={pill}
            >
              <RefreshCw className="me-2 h-4 w-4" /> New cover
            </button>
            {story.persona === "story_comic" && (
              <button
                data-testid="story-panels-button"
                onClick={drawPanels}
                disabled={busy || story.panels_status === "running"}
                className="inline-flex min-h-[44px] items-center rounded-full bg-brand px-5 text-sm text-white hover:bg-brand-hover disabled:opacity-60"
              >
                {story.panels_status === "running" ? (
                  <Loader2 className="me-2 h-4 w-4 animate-spin" />
                ) : (
                  <Images className="me-2 h-4 w-4" />
                )}
                {story.panels_status === "done" ? "Redraw panels" : "Draw panels"}
              </button>
            )}
          </div>

          <div className="mt-6 grid gap-4 rounded-2xl bg-page p-5 sm:grid-cols-2">
            <div>
              <label className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-ink-soft">
                <Languages className="h-3.5 w-3.5" /> Read in another language
              </label>
              <div className="mt-2 flex items-center gap-2">
                <Select value={lang} onValueChange={translate}>
                  <SelectTrigger data-testid="story-language-select" className="h-11 rounded-xl">
                    <SelectValue placeholder="Choose a language" />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    {languages.map((l) => (
                      <SelectItem key={l.code} value={l.code} data-testid={`story-language-${l.code}`}>
                        <span className="flex items-center gap-2">
                          <Flag code={l.code} /> {l.native} · {l.name}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {view && (
                  <button
                    data-testid="story-show-original"
                    onClick={() => {
                      setView(null);
                      setLang("");
                      setAudioUrl(null);
                    }}
                    className="shrink-0 rounded-full bg-white/10 px-4 py-2.5 text-xs hover:bg-white/20"
                  >
                    Original
                  </button>
                )}
              </div>
            </div>

            <div>
              <label className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-ink-soft">
                <Headphones className="h-3.5 w-3.5" /> Listen
              </label>
              <div className="mt-2 flex items-center gap-2">
                <Select value={voice} onValueChange={(v) => { setVoice(v); localStorage.setItem("cml_read_voice", v); setAudioUrl(null); }}>
                  <SelectTrigger data-testid="story-voice-select" className="h-11 rounded-xl">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {VOICES.map((v) => (
                      <SelectItem key={v.id} value={v.id} data-testid={`story-voice-${v.id}`}>
                        {v.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <VoicePreview voice={voice} testid="story-voice-preview" />
                <button
                  data-testid="story-narrate-button"
                  onClick={narrate}
                  disabled={busy}
                  className="shrink-0 inline-flex min-h-[44px] items-center rounded-full bg-brand px-5 text-sm text-white hover:bg-brand-hover disabled:opacity-60"
                >
                  {busy ? <Loader2 className="me-2 h-4 w-4 animate-spin" /> : <Headphones className="me-2 h-4 w-4" />}
                  Read aloud
                </button>
              </div>
              {audioUrl && (
                <div className="mt-3 flex flex-wrap items-center gap-3" data-testid="story-audio-player">
                  <button
                    data-testid="story-audio-toggle"
                    onClick={() => (playing ? audioRef.current?.pause() : audioRef.current?.play())}
                    className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-brand text-white"
                  >
                    {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                  </button>
                  <SpeedSelect value={speed} onChange={setSpeed} testid="story-speed-select" />
                  <audio
                    ref={audioRef}
                    src={audioUrl}
                    controls
                    onLoadedMetadata={() => {
                      if (audioRef.current) audioRef.current.playbackRate = speed;
                    }}
                    onPlay={() => setPlaying(true)}
                    onPause={() => setPlaying(false)}
                    onEnded={() => setPlaying(false)}
                    className="h-9 w-full"
                  />
                </div>
              )}
            </div>
          </div>

          {story.panels_status === "running" && (
            <p data-testid="story-panels-progress" className="mt-4 text-sm text-ink-soft">
              Drawing panel {(story.panels?.length || 0) + 1} of {story.panels_total}…
            </p>
          )}

          {story.panels?.length > 0 && (
            <div className="mt-6 grid gap-4 sm:grid-cols-2" data-testid="story-panels">
              {panels.map((p) => (
                <figure key={p.creation_id} className="overflow-hidden rounded-2xl border border-white/10 bg-page">
                  <AuthImage
                    creationId={p.creation_id}
                    alt={`Panel ${p.index}`}
                    className="aspect-square w-full object-cover"
                    testid={`story-panel-${p.index}`}
                  />
                  <figcaption className="p-3 text-xs text-ink-soft">
                    Panel {p.index} — {p.description.slice(0, 120)}
                  </figcaption>
                </figure>
              ))}
            </div>
          )}

          <div
            data-testid="story-text"
            dir="auto"
            className="mt-8 whitespace-pre-wrap border-t border-white/10 pt-8 text-sm leading-loose text-ink-soft"
          >
            {shownText}
          </div>
        </div>
      </div>
    </div>
  );
}
